import React, { useEffect, useMemo, useRef, useState } from "react";
import { useWindowDimensions } from "../../../../../../common-deprecated/hooks";
import useSlider from "../../../../../../common-deprecated/hooks/useSlider";
import * as SliderStyles from "../styles/Slider";
import ImageSliderIndex from "./ImageSliderIndex";
import Icon from "../../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import Placeholder from "../../../../../../common-deprecated/styles/v2/toyota/components/Placeholder";
import IconPlaceholder from "../../../../../../common-deprecated/svg/toyota/aem/placeholder.svg";
import SrOnly from "../../../../../../common-deprecated/styles/v2/toyota/utilities/ScreenreaderOnly";
import { EquipmentV2Label } from "../../../utils/labels";
import { getCarDBAssetSrcSet } from "../../../../../shared-logic/utils/imageUtils";
import { useEquipmentV2Selector } from "../../../redux/store";

type EquipmentImageSliderType = {
    assets?: string[];
    imageAlt: string;
    equipmentId: string;
};

const EquipmentImageSlider = (props: EquipmentImageSliderType): JSX.Element => {
    const { assets, imageAlt, equipmentId } = props;

    const settings = useEquipmentV2Selector((state) => state.commonSettings);

    const [activeImage, setActiveImage] = useState<number>(0);

    const viewPortRef = useRef<HTMLDivElement>(null);

    // Set the viewport width of the slider to propagate to useSlider.
    const windowWidth = useWindowDimensions().width;
    const [viewportWidth, setViewportWidth] = useState<number>(0);

    useEffect(() => {
        if (viewPortRef.current) {
            const newViewportWidth = viewPortRef.current.getBoundingClientRect().width;
            if (newViewportWidth !== viewportWidth) setViewportWidth(newViewportWidth);
        }
    }, [windowWidth]);

    const sliderProps = useSlider((assets?.length || 1) * viewportWidth, viewportWidth, viewportWidth);
    const { startTouchDrag, startMouseDrag, sliderX, isScrolling, enableCssTransition } = sliderProps;
    const { leftArrowEnabled, rightArrowEnabled, leftArrowClickPage, rightArrowClickPage } = sliderProps;
    const { onTransitionEnd } = sliderProps;

    const placeHolderImg = (
        <Placeholder key={equipmentId}>
            <IconPlaceholder />
        </Placeholder>
    );

    const list = useMemo(
        () =>
            assets?.length
                ? assets.map((url, index) =>
                      url.length ? (
                          // For some reason only using the url as key makes the slider break on some strange ocassions.
                          <SliderStyles.ImageWrapper
                              aria-roledescription={`slide-${equipmentId}-${index}`}
                              id={`slide-${equipmentId}-${index}`}
                              aria-label={`slide ${index}`}
                              key={`${url}-${index}`} // eslint-disable-line react/no-array-index-key
                          >
                              <SliderStyles.Image
                                  {...getCarDBAssetSrcSet(
                                      settings,
                                      [
                                          { width: 512, height: 512 },
                                          { width: 254, height: 254 },
                                      ],
                                      url,
                                  )}
                                  loading="lazy"
                                  width="512"
                                  height="512"
                                  sizes="(max-width: 576px) 512px, 254px"
                                  alt={`${imageAlt} ${index}`}
                              />
                          </SliderStyles.ImageWrapper>
                      ) : (
                          placeHolderImg
                      ),
                  )
                : placeHolderImg,
        [assets],
    );

    // Save the active image in state, based on calculating the slider position.
    useEffect(() => {
        const activeIndex =
            sliderX === 0
                ? 0
                : Math.round(Math.abs(sliderX / (((assets?.length || 1) * viewportWidth) / (assets?.length || 1))));
        if (activeIndex !== activeImage) setActiveImage(activeIndex);
    }, [sliderX, activeImage]);

    return (assets?.length || 1) > 1 ? (
        <SliderStyles.Slider>
            <SliderStyles.SliderLeft
                type="button"
                aria-labelledby={`button-previous-${equipmentId}`}
                show={leftArrowEnabled}
                onClick={leftArrowClickPage}
            >
                <Icon variant="chevronLeft" />
                <SrOnly id={`button-previous-${equipmentId}`}>
                    <EquipmentV2Label label="ariaPreviousImage" />
                </SrOnly>
            </SliderStyles.SliderLeft>
            <SliderStyles.SliderRight
                type="button"
                aria-labelledby={`button-next-${equipmentId}`}
                show={rightArrowEnabled}
                onClick={rightArrowClickPage}
            >
                <Icon variant="chevronRight" />
                <SrOnly id={`button-next-${equipmentId}`}>
                    <EquipmentV2Label label="ariaNextImage" />
                </SrOnly>
            </SliderStyles.SliderRight>

            <SliderStyles.SliderViewport ref={viewPortRef} role="group" aria-live="polite">
                <SliderStyles.Track
                    style={{
                        width: `${(assets?.length || 1) * 100}%`,
                    }}
                    position="absolute"
                    left={sliderX}
                    enableTransition={enableCssTransition}
                    onTransitionEnd={onTransitionEnd}
                    onTouchStart={startTouchDrag}
                    onMouseDown={startMouseDrag}
                    onClick={(event) => {
                        if (isScrolling.current) {
                            event.preventDefault();
                            event.stopPropagation();
                        }
                    }}
                >
                    {list}
                </SliderStyles.Track>
                <SliderStyles.SliderBottom>
                    {!!assets && assets?.length > 1 && (
                        <SliderStyles.SliderIndex>
                            <ImageSliderIndex itemCount={assets?.length} activeItemIndex={activeImage} />
                        </SliderStyles.SliderIndex>
                    )}
                </SliderStyles.SliderBottom>
            </SliderStyles.SliderViewport>
        </SliderStyles.Slider>
    ) : (
        <SliderStyles.ImageViewPort role="group" aria-live="polite">
            <SliderStyles.ImageTrack>{list}</SliderStyles.ImageTrack>
        </SliderStyles.ImageViewPort>
    );
};

export default EquipmentImageSlider;
